/**
 * character-manager.js
 * Quản lý Kho Nhân Vật (Character Roster & Casting Board) cho Voxel Anime Studio.
 * Hỗ trợ lưu trữ, đặt tên, chỉnh sửa, nhân bản và tải nhân vật từ LocalStorage & File.
 */

const STORAGE_KEY = 'VAS_SAVED_CHARACTERS';

export class CharacterManager {
  constructor() {
    this.characters = new Map();
    this.initDefaultCharacters();
    this.loadFromStorage();
  }

  /**
   * Khởi tạo các nhân vật mặc định từ kho Preset đậm chất Việt Nam
   */
  initDefaultCharacters() {
    const defaultList = [
      {
        id: 'char_teacher',
        name: 'Cô Giáo Lan (Áo Dài)',
        presetKey: 'TEACHER',
        bodyType: 'normal',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#1e293b',
          shirt: '#ec4899',
          pants: '#ffffff',
          shoes: '#0f172a'
        },
        features: {
          outfit: 'ao_dai',
          hairStyle: 'long_hair',
          hat: 'none',
          eyewear: 'kinh_can',
          mask: 'none',
          bodyAccessory: 'none',
          heldItem: 'clipboard'
        },
        voice: { pitch: 1.2, rate: 1.0, gender: 'female' },
        isBuiltIn: true
      },
      {
        id: 'char_bac_ba',
        name: 'Bác Ba Quán Nước (Áo Bà Ba)',
        presetKey: 'BAC_BA_BA',
        bodyType: 'normal',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#64748b',
          shirt: '#475569',
          pants: '#1e293b',
          shoes: '#0f172a'
        },
        features: {
          outfit: 'ao_ba_ba',
          hairStyle: 'bald',
          hat: 'non_la_tron',
          eyewear: 'none',
          mask: 'none',
          bodyAccessory: 'khan_ran',
          heldItem: 'coc_tra_da'
        },
        voice: { pitch: 0.85, rate: 0.95, gender: 'male' },
        isBuiltIn: true
      },
      {
        id: 'char_grab',
        name: 'Chú Grab Tài Xế',
        presetKey: 'TAI_XE_GRAB',
        bodyType: 'normal',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#1e293b',
          shirt: '#15803d',
          pants: '#18181b',
          shoes: '#0f172a'
        },
        features: {
          outfit: 'grab',
          hairStyle: 'dan_truong',
          hat: 'mu_bh_grab',
          eyewear: 'none',
          mask: 'khau_trang_trang',
          bodyAccessory: 'tui_cheo',
          heldItem: 'none'
        },
        voice: { pitch: 0.95, rate: 1.05, gender: 'male' },
        isBuiltIn: true
      },
      {
        id: 'char_xanh_sm',
        name: 'Anh Tài Xế Xanh SM',
        presetKey: 'TAI_XE_XANH_SM',
        bodyType: 'normal',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#1e293b',
          shirt: '#0284c7',
          pants: '#1e293b',
          shoes: '#0f172a'
        },
        features: {
          outfit: 'xanh_sm',
          hairStyle: 'elvis',
          hat: 'mu_luoi_trai',
          eyewear: 'kinh_den_ngau',
          mask: 'none',
          bodyAccessory: 'tui_cheo',
          heldItem: 'none'
        },
        voice: { pitch: 1.0, rate: 1.1, gender: 'male' },
        isBuiltIn: true
      },
      {
        id: 'char_student_vn',
        name: 'Học Sinh Đội Viên (Khăn Quàng)',
        presetKey: 'HOC_SINH_VN',
        bodyType: 'child',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#1e293b',
          shirt: '#ffffff',
          pants: '#1e3a8a',
          shoes: '#0f172a',
          scarf: '#dc2626'
        },
        features: {
          outfit: 'hoc_sinh_vn',
          hairStyle: 'spiky',
          hat: 'mu_bh_grab',
          eyewear: 'none',
          mask: 'none',
          bodyAccessory: 'ba_lo',
          heldItem: 'none'
        },
        voice: { pitch: 1.3, rate: 1.0, gender: 'female' },
        isBuiltIn: true
      },
      {
        id: 'char_tra_da',
        name: 'Bà Bán Trà Đá',
        presetKey: 'NGUOI_BAN_TRA_DA',
        bodyType: 'normal',
        isArticulated: true,
        colors: {
          skin: '#fcd5b5',
          hair: '#475569',
          shirt: '#ca8a04',
          pants: '#1c1917',
          shoes: '#0f172a'
        },
        features: {
          outfit: 'ao_ba_ba',
          hairStyle: 'long_hair',
          hat: 'non_la',
          eyewear: 'none',
          mask: 'none',
          bodyAccessory: 'khan_ran',
          heldItem: 'am_tra_bat_trang'
        },
        voice: { pitch: 1.05, rate: 0.95, gender: 'female' },
        isBuiltIn: true
      },
      {
        id: 'pet_dog_shiba',
        name: 'Chó Vàng Shiba',
        type: 'animal',
        speciesKey: 'DOG',
        variantKey: 'shiba',
        scale: 1.0,
        voice: { pitch: 1.5, rate: 1.0, gender: 'male' },
        isBuiltIn: true
      },
      {
        id: 'pet_cat_calico',
        name: 'Mèo Tam Thể Calico',
        type: 'animal',
        speciesKey: 'CAT',
        variantKey: 'calico',
        scale: 1.0,
        voice: { pitch: 1.6, rate: 1.2, gender: 'female' },
        isBuiltIn: true
      }
    ];

    defaultList.forEach((c) => this.characters.set(c.id, c));
  }

  /**
   * Đọc danh sách nhân vật đã lưu từ LocalStorage
   */
  loadFromStorage() {
    try {
      const dataStr = localStorage.getItem(STORAGE_KEY);
      if (dataStr) {
        const savedList = JSON.parse(dataStr);
        if (Array.isArray(savedList)) {
          savedList.forEach((item) => {
            if (item && item.id) {
              this.characters.set(item.id, item);
            }
          });
        }
      }
    } catch (err) {
      console.warn('Lỗi khi nạp nhân vật từ LocalStorage:', err);
    }
  }

  /**
   * Lưu các nhân vật tùy biến vào LocalStorage
   */
  saveToStorage() {
    try {
      // Chỉ lưu nhân vật tự tạo hoặc bản ghi đè
      const listToSave = Array.from(this.characters.values());
      localStorage.setItem(STORAGE_KEY, JSON.stringify(listToSave));
    } catch (err) {
      console.warn('Lỗi khi lưu nhân vật vào LocalStorage:', err);
    }
  }

  /**
   * Lấy toàn bộ danh sách nhân vật
   * @returns {Array<object>}
   */
  getAllCharacters() {
    return Array.from(this.characters.values());
  }

  /**
   * Lấy chi tiết nhân vật theo ID
   * @param {string} id
   * @returns {object|null}
   */
  getCharacter(id) {
    return this.characters.get(id) || null;
  }

  /**
   * Trích xuất thông tin cấu hình hoàn chỉnh từ đối tượng BlockyAvatar đang chạy
   * @param {BlockyAvatar} avatar
   * @param {string} customName
   * @returns {object} Character profile
   */
  extractFromAvatar(avatar, customName = 'Nhân Vật Mới') {
    if (!avatar) return null;
    const cfg = avatar.config || {};
    const features = { ...(cfg.features || {}) };
    const colors = { ...(cfg.colors || {}) };

    // Tự động suy ra các giá trị hiện hành từ slot đính kèm
    return {
      id: 'char_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7),
      name: customName.trim() || 'Nhân Vật Mới',
      presetKey: cfg.id || 'TEACHER',
      bodyType: cfg.bodyType || 'normal',
      isArticulated: avatar.isArticulated !== undefined ? avatar.isArticulated : true,
      colors: colors,
      features: features,
      voice: {
        pitch: cfg.voice?.pitch ?? 1.0,
        rate: cfg.voice?.rate ?? 1.0,
        gender: cfg.voice?.gender || 'female'
      },
      isBuiltIn: false,
      updatedAt: new Date().toISOString()
    };
  }

  /**
   * Lưu hoặc cập nhật một nhân vật vào kho
   * @param {object} profile
   * @returns {object} profile đã lưu
   */
  saveCharacter(profile) {
    if (!profile) return null;
    if (!profile.id) {
      profile.id = 'char_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
    }
    profile.updatedAt = new Date().toISOString();
    this.characters.set(profile.id, { ...profile });
    this.saveToStorage();
    return profile;
  }

  /**
   * Nhân bản một nhân vật có sẵn
   * @param {string} id
   * @returns {object|null}
   */
  duplicateCharacter(id) {
    const orig = this.getCharacter(id);
    if (!orig) return null;

    const copy = JSON.parse(JSON.stringify(orig));
    copy.id = 'char_' + Date.now() + '_' + Math.random().toString(36).substring(2, 7);
    copy.name = `${orig.name} (Bản sao)`;
    copy.isBuiltIn = false;
    copy.updatedAt = new Date().toISOString();

    this.characters.set(copy.id, copy);
    this.saveToStorage();
    return copy;
  }

  /**
   * Xóa nhân vật khỏi danh sách
   * @param {string} id
   * @returns {boolean}
   */
  deleteCharacter(id) {
    if (!this.characters.has(id)) return false;
    this.characters.delete(id);
    this.saveToStorage();
    return true;
  }

  /**
   * Đăng ký một hồ sơ thú cưng vào kho diễn viên
   * @param {object} profile
   * @returns {object}
   */
  registerAnimal(profile) {
    if (!profile) return null;
    const pet = {
      id: profile.id || `pet_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      name: profile.name || 'Thú Cưng',
      type: 'animal',
      speciesKey: (profile.speciesKey || 'DOG').toUpperCase(),
      variantKey: profile.variantKey || null,
      scale: profile.scale || 1.0,
      isBuiltIn: !!profile.isBuiltIn,
      updatedAt: new Date().toISOString()
    };
    this.characters.set(pet.id, pet);
    this.saveToStorage();
    return pet;
  }

  /**
   * Chuyển đổi Profile thành options chuẩn để khởi tạo BlockyAvatar hoặc BlockyAnimal
   * @param {object} profile
   * @returns {{ type: string, presetKey?: string, speciesKey?: string, variantKey?: string, scale?: number, options: object }}
   */
  toAvatarInitParams(profile) {
    if (!profile) return { type: 'humanoid', presetKey: 'TEACHER', options: {} };
    if (profile.type === 'animal') {
      return {
        type: 'animal',
        speciesKey: (profile.speciesKey || 'DOG').toUpperCase(),
        variantKey: profile.variantKey || null,
        scale: profile.scale || 1.0,
        options: {
          name: profile.name,
          scale: profile.scale || 1.0
        }
      };
    }
    return {
      type: 'humanoid',
      presetKey: profile.presetKey || 'TEACHER',
      options: {
        name: profile.name,
        bodyType: profile.bodyType || 'normal',
        isArticulated: profile.isArticulated !== undefined ? profile.isArticulated : true,
        colors: { ...(profile.colors || {}) },
        features: { ...(profile.features || {}) },
        voice: { ...(profile.voice || {}) }
      }
    };
  }
}
